import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import AppLogo from './AppLogo';
import NotificationBadge from './NotificationBadge';
import authService from '../../services/cognitoService';
import { cn } from '../../utils/cn';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const DOCUMENTS_GROUP_PATHS = [
  '/write-contract',
  '/model-library',
  '/system-models',
  '/contract-generator',
  '/user-templates',
  '/create-template',
  '/edit-template',
  '/uploaded-documents',
];

const CRM_GROUP_PATHS = ['/crm/leads', '/crm/respostas'];

const CHROME_EXTENSION_URL =
  'https://chromewebstore.google.com/detail/neo-modelos-%E2%80%94-crm-whatsap/gchhclbflhibaokfchfcfnbpfiikolah?authuser=0&hl=en';

const MOBILE_SIDEBAR_MQ = '(max-width: 1023px)';

const NavigationSidebar = ({
  isCollapsed = false,
  onToggle,
  onCloseMobile,
  forceDocumentsOpen = false,
  forceCrmOpen = false,
}) => {
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const isDocumentsRoute = DOCUMENTS_GROUP_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );
  const isCrmRoute = CRM_GROUP_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );
  const [documentsOpen, setDocumentsOpen] = useState(
    isDocumentsRoute || forceDocumentsOpen
  );
  const [crmOpen, setCrmOpen] = useState(isCrmRoute || forceCrmOpen);

  const handleMobileNavClick = useCallback(() => {
    if (window.matchMedia(MOBILE_SIDEBAR_MQ).matches) {
      onCloseMobile?.();
    }
  }, [onCloseMobile]);

  useEffect(() => {
    if (isDocumentsRoute || forceDocumentsOpen) {
      setDocumentsOpen(true);
    }
  }, [forceDocumentsOpen, isDocumentsRoute]);

  useEffect(() => {
    if (isCrmRoute || forceCrmOpen) {
      setCrmOpen(true);
    }
  }, [forceCrmOpen, isCrmRoute]);

  const documentsChildren = useMemo(
    () => [
      {
        id: 'write-contract',
        label: 'Criar Documento',
        path: '/write-contract',
        icon: 'PenLine',
        tooltip: 'Editor livre para elaborar documento com assistente NeoAi',
      },
      {
        id: 'model-library',
        label: 'Biblioteca de Modelos',
        path: '/model-library',
        icon: 'Library',
        tooltip: 'Modelos do sistema e modelos personalizados',
      },
      {
        id: 'uploaded-documents',
        label: 'Enviar para Assinatura',
        path: '/uploaded-documents',
        icon: 'Upload',
        tooltip: 'Enviar PDFs prontos para assinatura digital',
      },
    ],
    []
  );

  const crmChildren = useMemo(
    () => [
      {
        id: 'crm-leads',
        label: 'Leads',
        path: '/crm/leads',
        icon: 'Kanban',
        tooltip: 'Pipeline de leads sincronizado com a extensão WhatsApp',
      },
      {
        id: 'crm-respostas',
        label: 'Respostas Padrão',
        path: '/crm/respostas',
        icon: 'MessageSquareText',
        tooltip: 'Respostas rápidas usadas no WhatsApp',
      },
    ],
    []
  );

  const navigationItems = useMemo(
    () => [
      {
        id: 'home',
        label: 'Home',
        path: '/contract-dashboard',
        icon: 'Home',
        tooltip: 'Painel inicial e lista de documentos',
      },
      {
        id: 'license-store',
        label: 'Comprar Licença',
        path: '/license-store',
        icon: 'ShoppingCart',
        tooltip: 'Compra única ou assinatura mensal de planos',
      },
      {
        id: 'account-management',
        label: 'Minha Conta',
        path: '/account-management',
        icon: 'User',
        tooltip: 'Gerenciar perfil e configurações',
      },
      {
        id: 'notifications',
        label: 'Notificações',
        path: '/notifications',
        icon: 'Bell',
        tooltip: 'Ver notificações do sistema',
        badgeCount: unreadCount,
      },
      {
        id: 'configurations',
        label: 'Configurações',
        path: '/configurations',
        icon: 'Settings',
        tooltip: 'Configurar logo de cabeçalho e logo de rodapé dos documentos',
      },
      {
        id: 'license',
        label: 'Minha Licença',
        path: '/my-license',
        icon: 'BadgeCheck',
        tooltip: 'Ver licença, Neo Coins e cancelar assinatura mensal',
      },
      {
        id: 'customers',
        label: 'Clientes',
        path: '/customers',
        icon: 'Users',
        tooltip: 'Cadastrar e gerenciar clientes',
      },
    ],
    [unreadCount]
  );

  const isActive = (path) => location?.pathname === path;

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      authService.logout();
    }
  }, []);

  const linkClassName = (active) =>
    cn(
      'relative flex items-center px-3 py-2.5 rounded-lg transition-smooth group',
      active
        ? 'bg-[var(--color-sidebar-active)] text-primary font-semibold shadow-soft-sm before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-[3px] before:rounded-full before:bg-[var(--color-sidebar-active-border)]'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    );

  const subLinkClassName = (active) =>
    cn(
      'flex items-center pl-10 pr-3 py-2 rounded-lg text-sm transition-smooth',
      active
        ? 'bg-primary/10 text-primary font-semibold'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    );

  return (
    <>
      <button
        onClick={onToggle}
        className="fixed top-4 left-4 z-50 lg:hidden flex items-center justify-center w-12 h-12 bg-card text-foreground rounded-xl shadow-elevation-md border border-border transition-smooth hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        aria-label={isCollapsed ? 'Abrir menu de navegação' : 'Fechar menu de navegação'}
      >
        <Icon name={isCollapsed ? 'Menu' : 'X'} size={24} />
      </button>

      <aside
        data-tour="tour-sidebar"
        className={`fixed top-0 left-0 h-full w-60 flex flex-col bg-card/95 backdrop-blur-xl border-r border-border shadow-elevation-md transition-smooth z-[1000] ${
          isCollapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'
        }`}
      >
        {/* Header */}
        <div
          data-tour="tour-brand"
          className="sidebar-header flex items-center justify-center h-20 px-4 border-b border-border"
        >
          <div className="sidebar-logo flex items-center justify-center w-12 h-12 rounded-xl transition-smooth">
            <AppLogo size="sm" variant="mark" />
          </div>
          <span className="ml-3 text-lg font-bold text-foreground tracking-tight">
            neo-modelos
          </span>
        </div>

        {/* Navigation */}
        <nav
          className="flex flex-col p-3 space-y-1 overflow-y-auto"
          aria-label="Navegação principal"
        >
          <Link
            to="/contract-dashboard"
            data-tour="tour-home"
            className={linkClassName(isActive('/contract-dashboard'))}
            title="Painel inicial e lista de documentos"
            aria-current={isActive('/contract-dashboard') ? 'page' : undefined}
            onClick={handleMobileNavClick}
          >
            <Icon name="Home" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-medium font-body">Home</span>
          </Link>

          <div className="space-y-1" data-tour="tour-documents">
            <button
              type="button"
              onClick={() => setDocumentsOpen((open) => !open)}
              className={cn(
                linkClassName(isDocumentsRoute && !documentsOpen),
                'w-full',
                isDocumentsRoute && documentsOpen && 'text-primary bg-[var(--color-sidebar-active)] font-semibold'
              )}
              aria-expanded={documentsOpen}
              title="Criar e gerenciar documentos"
            >
              <Icon name="Files" size={20} className="flex-shrink-0" />
              <span className="ml-3 text-sm font-medium font-body flex-1 text-left">
                Documentos
              </span>
              <Icon
                name={documentsOpen ? 'ChevronUp' : 'ChevronDown'}
                size={16}
                className="flex-shrink-0 opacity-70"
              />
            </button>

            {documentsOpen ? (
              <div className="space-y-0.5" role="group" aria-label="Documentos">
                {documentsChildren.map((child) => (
                  <Link
                    key={child.id}
                    to={child.path}
                    data-tour={`tour-${child.id}`}
                    className={subLinkClassName(isActive(child.path))}
                    title={child.tooltip}
                    aria-current={isActive(child.path) ? 'page' : undefined}
                    onClick={handleMobileNavClick}
                  >
                    <Icon name={child.icon} size={16} className="flex-shrink-0 mr-2.5" />
                    <span className="font-body">{child.label}</span>
                  </Link>
                ))}
              </div>
            ) : null}
          </div>

          <div className="space-y-1" data-tour="tour-crm">
            <button
              type="button"
              onClick={() => setCrmOpen((open) => !open)}
              className={cn(
                linkClassName(isCrmRoute && !crmOpen),
                'w-full',
                isCrmRoute && crmOpen && 'text-primary bg-[var(--color-sidebar-active)] font-semibold'
              )}
              aria-expanded={crmOpen}
              title="CRM sincronizado com a extensão WhatsApp"
            >
              <Icon name="ContactRound" size={20} className="flex-shrink-0" />
              <span className="ml-3 text-sm font-medium font-body flex-1 text-left">
                CRM Neo Documentos
              </span>
              <Icon
                name={crmOpen ? 'ChevronUp' : 'ChevronDown'}
                size={16}
                className="flex-shrink-0 opacity-70"
              />
            </button>

            {crmOpen ? (
              <div className="space-y-0.5" role="group" aria-label="CRM Neo Documentos">
                {crmChildren.map((child) => (
                  <Link
                    key={child.id}
                    to={child.path}
                    data-tour={`tour-${child.id}`}
                    className={subLinkClassName(isActive(child.path))}
                    title={child.tooltip}
                    aria-current={isActive(child.path) ? 'page' : undefined}
                    onClick={handleMobileNavClick}
                  >
                    <Icon name={child.icon} size={16} className="flex-shrink-0 mr-2.5" />
                    <span className="font-body">{child.label}</span>
                  </Link>
                ))}
              </div>
            ) : null}
          </div>

          {navigationItems.slice(1).map((item) => (
            <Link
              key={item.id}
              to={item.path}
              data-tour={`tour-${item.id}`}
              className={linkClassName(isActive(item.path))}
              title={item.tooltip}
              aria-current={isActive(item.path) ? 'page' : undefined}
              onClick={handleMobileNavClick}
            >
              <Icon name={item.icon} size={20} className="flex-shrink-0" />
              <span className="ml-3 text-sm font-medium font-body">
                {item.label}
              </span>
              {item.badgeCount > 0 && (
                <NotificationBadge count={item.badgeCount} className="ml-auto" />
              )}
            </Link>
          ))}
        </nav>

        {/* Extension CTA + Logout */}
        <div className="mt-auto p-3 border-t border-border space-y-1">
          <a
            href={CHROME_EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-tour="tour-chrome-extension"
            className={cn(
              linkClassName(false),
              'text-primary hover:bg-primary/10 hover:text-primary font-semibold'
            )}
            title="Baixar a extensão CRM para WhatsApp Web"
            onClick={handleMobileNavClick}
          >
            <Icon name="Puzzle" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-semibold font-body flex-1 min-w-0 leading-snug">
              Baixe a nossa extensão
            </span>
            <span className="ml-2 shrink-0 inline-flex items-center rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
              Novo
            </span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-smooth"
            aria-label="Sair da conta"
          >
            <Icon name="LogOut" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-medium font-body">Sair</span>
          </button>
        </div>
      </aside>

      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-background z-[999] lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default NavigationSidebar;
